/**
 * RunningAnalysisView — shared UI for RunningAnalysis and SharedRunningAnalysis.
 *
 * Accepts workoutData plus header slot props (headerLeft / headerRight / headerActions)
 * so each
 * page can render its own title block and action buttons while sharing all stat boxes,
 * charts, and tab logic.
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { useReactToPrint } from 'react-to-print';
import PlanPrintContent from './PlanPrintContent';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  useTheme,
  Tabs,
  Tab,
  Paper,
  useMediaQuery,
  Alert,
  Grid,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Button,
  IconButton,
  Popover,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Link,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ImageIcon from '@mui/icons-material/Image';
import WatchIcon from '@mui/icons-material/Watch';
import TuneIcon from '@mui/icons-material/Tune';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FiberSmartRecordIcon from '@mui/icons-material/FiberSmartRecord';
import StraightenIcon from '@mui/icons-material/Straighten';
import { tokens } from '../../theme';
import { attentionPulse, pulseAnimation, PULSE_TOTAL_MS } from '../../utils/attentionPulse';
import { useTranslation } from 'react-i18next';
import {
  getUnits,
  formatDistance,
  formatPace,
  formatDuration,
  formatElevation,
  formatHeartRate,
  formatVerticalSpeed,
} from '../../utils/units';
import {
  getRunningRaceGpxPoints, downloadWatchGpx, downloadPlanFit,
  downloadPredictionWatchGpx, downloadPredictionPlanFit,
  createPredictionWatchPlan, createAnalysisWatchPlan,
  getPredictionWatchPlan, getAnalysisWatchPlan,
  setAnalysisManualCheckpoints, setPredictionManualCheckpoints,
} from '../../api/runningApi';
import { downloadPlanWallpaper } from '../../utils/planWallpaperGenerator';
import { buildMinimalRacePlan } from '../../utils/racePlanMinimal';
import StatBox from '../StatBox';
import RunningWorkoutChartsSection from './RunningWorkoutChartsSection';
import RunningRouteMap from './RunningRouteMap';
import RunningCheckpointsGrid from './RunningCheckpointsGrid';
import RunningClimbsGrid from './RunningClimbsGrid';
import RunningDescendsGrid from './RunningDescendsGrid';
import RacePlanGrid from './RacePlanGrid';
import WatchPlanDialog, { MKPACELIVE_MANUAL_URL, MKPACELIVE_QUICKGUIDE_URL } from './WatchPlanDialog';
import ManualCheckpointDialog from './ManualCheckpointDialog';
import { getPlanBoundaryMarkers } from '../../utils/racePlanBoundaries';

// Icons
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import TimerIcon from '@mui/icons-material/Timer';
import SpeedIcon from '@mui/icons-material/Speed';
import TerrainIcon from '@mui/icons-material/Terrain';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';

// ---------------------------------------------------------------------------

// Compact stop-duration label for the Moving Time subtitle, e.g. 41 s → "1m",
// 3050 s → "51m", 3720 s → "1h02".
const formatShortStop = (seconds) => {
  const s = Math.max(0, Math.round(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h > 0) return `${h}h${String(m).padStart(2, '0')}`;
  return `${m}m`;
};

// Split total stopped time into "at checkpoints" vs "scattered between them".
// Predictions carry the split; real analyses derive it from the actual per-checkpoint
// stopping times (excluding Start/Finish, which aren't aid stations). When a workout
// has no aid-station checkpoints (e.g. a plain run or a timed-loop event), the split
// isn't meaningful, so we return just the total stopped time. Everything null →
// the box shows "N stops".
const deriveStopSplit = (wd) => {
  if (!wd) return { checkpoint_stop_time_s: null, free_stop_time_s: null, total_stop_time_s: null };
  if (wd.checkpoint_stop_time_s != null) {
    return { checkpoint_stop_time_s: wd.checkpoint_stop_time_s, free_stop_time_s: wd.free_stop_time_s ?? 0, total_stop_time_s: null };
  }
  const total = wd.total_paused_time || 0;
  if (total <= 0) return { checkpoint_stop_time_s: null, free_stop_time_s: null, total_stop_time_s: null };
  // Real aid-station checkpoints only — exclude auto-detected 'unplanned_stop' markers.
  const acs = (wd.analyzed_checkpoints || []).filter((c) => (c.type || 'checkpoint') === 'checkpoint');
  if (acs.length === 0) {
    return { checkpoint_stop_time_s: null, free_stop_time_s: null, total_stop_time_s: total };
  }
  const maxKm = Math.max(...acs.map((c) => c.distance_km || 0), 0);
  const isEdge = (c) => {
    const km = c.distance_km || 0;
    const svc = (c.services || '').toLowerCase();
    return km <= 0.3 || svc.includes('start') || svc.includes('finish') || (maxKm && km >= maxKm - 0.3);
  };
  const cp = Math.min(
    total,
    acs.filter((c) => !isEdge(c)).reduce((s, c) => s + (c.stopping_time_seconds || 0), 0)
  );
  return { checkpoint_stop_time_s: cp, free_stop_time_s: Math.max(0, total - cp), total_stop_time_s: null };
};

// Garmin Workout (.fit) and Watch GPX exports, hidden while mkpaceLIVE covers the
// same ground more completely. Set to true to restore them.
const SHOW_LEGACY_WATCH_EXPORTS = false;

const RunningAnalysisView = ({ workoutData, headerLeft, headerRight, headerActions, headerNav, belowHeader, buildRacePlanFn }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const pulse = attentionPulse(colors);
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ── Derived analysis data ──────────────────────────────────────────────
  const [chunks, setChunks] = useState([]);
  const [summaryStats, setSummaryStats] = useState({});
  const [race, setRace] = useState(null);
  const [analyzedCheckpoints, setAnalyzedCheckpoints] = useState([]);
  const [climbs, setClimbs] = useState([]);
  const [descents, setDescents] = useState([]);
  const [racePlanSegments, setRacePlanSegments] = useState([]);
  const [planGranularity, setPlanGranularity] = useState('detailed');

  /**
   * Load a plan and pick the view that suits it.
   *
   * With aid stations, minimal is what a runner actually races off — one row per
   * checkpoint-to-checkpoint leg, which is also what goes on the watch. Detailed
   * is there to drill into the terrain, not to read at a glance. Without aid
   * stations minimal collapses to a single Start→Finish row, so detailed is the
   * only view with anything in it.
   */
  const loadRacePlanSegments = (segs) => {
    const list = segs || [];
    const gran = list.some((seg) => seg.segment_type === 'checkpoint') ? 'minimal' : 'detailed';
    setRacePlanSegments(list);
    setPlanGranularity(gran);
    // Returned because setState is async: a caller that just built the plan needs the
    // granularity NOW, not on the next render.
    return gran;
  };
  const [originalFitPoints, setOriginalFitPoints] = useState(null);
  const [movingSummary, setMovingSummary] = useState({
    moving_avg_pace: null,
    moving_avg_gap: null,
    computed_avg_gap: null,
    moving_avg_hr: null,
    moving_avg_asc_vspeed: null,
    moving_avg_desc_vspeed: null,
  });

  // ── UI state ───────────────────────────────────────────────────────────
  const [currentTab, setCurrentTab] = useState(0);
  const [units, setUnits] = useState(getUnits());
  const [checkpointZoom, setCheckpointZoom] = useState(null);
  const [climbZoom, setClimbZoom] = useState(null);
  const [activeClimbRange, setActiveClimbRange] = useState(null);
  const [descentZoom, setDescentZoom] = useState(null);
  // Anchors for the recording-mode / distance-correction info popovers (icon buttons in the header).
  const [recNoticeAnchor, setRecNoticeAnchor] = useState(null);
  const [distNoticeAnchor, setDistNoticeAnchor] = useState(null);
  const [activeDescentRange, setActiveDescentRange] = useState(null);
  const [chartDistZoom, setChartDistZoom] = useState(null);
  const [segmentHighlight, setSegmentHighlight] = useState(null);
  const [segmentZoom, setSegmentZoom] = useState(null);
  const [showGpxRoute, setShowGpxRoute] = useState(false);
  const [gpxRoutePoints, setGpxRoutePoints] = useState(null);
  const [racePlanLoading, setRacePlanLoading] = useState(false);
  const [racePlanTriggered, setRacePlanTriggered] = useState(false);

  // ── Plan print ─────────────────────────────────────────────────────────
  const planPrintRef = useRef();
  const handlePrintPlan = useReactToPrint({ contentRef: planPrintRef });

  const [watchGpxLoading, setWatchGpxLoading] = useState(false);
  const [watchGpxInfoAnchor, setWatchGpxInfoAnchor] = useState(null);
  const [planZwoLoading, setPlanZwoLoading] = useState(false);
  const [planZwoInfoAnchor, setPlanZwoInfoAnchor] = useState(null);
  const [planGranularityInfoAnchor, setPlanGranularityInfoAnchor] = useState(null);
  const handleDownloadWatchGpx = async () => {
    const sessionId = workoutData?.analysis_session_id;
    const predictionId = workoutData?.prediction_session_id;
    if (!sessionId && !predictionId) return;
    setWatchGpxLoading(true);
    try {
      const response = sessionId
        ? await downloadWatchGpx(sessionId)
        : await downloadPredictionWatchGpx(predictionId);
      const disposition = response.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      const filename = match ? match[1].replace(/['"]/g, '') : 'mkpace_watch.gpx';
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/gpx+xml' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // silently ignore — user will see no download
    } finally {
      setWatchGpxLoading(false);
    }
  };

  const handleDownloadPlanFit = async () => {
    const sessionId = workoutData?.analysis_session_id;
    const predictionId = workoutData?.prediction_session_id;
    if (!sessionId && !predictionId) return;
    setPlanZwoLoading(true);
    try {
      const response = sessionId
        ? await downloadPlanFit(sessionId)
        : await downloadPredictionPlanFit(predictionId);
      const disposition = response.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      const filename = match ? match[1].replace(/['"]/g, '') : 'mkpace_plan.fit';
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // silently ignore
    } finally {
      setPlanZwoLoading(false);
    }
  };

  // ── Manual (user-placed) checkpoints ──────────────────────────────────
  // The saved list lives on the server as a plan INPUT; the built segments carry
  // it back with is_manual/manual_id, so the current list is derived from them
  // rather than tracked separately and risking the two drifting apart.
  const [checkpointDialogOpen, setCheckpointDialogOpen] = useState(false);
  const [editingCheckpoint, setEditingCheckpoint] = useState(null);
  const [checkpointBusy, setCheckpointBusy] = useState(false);

  // A user-placed point has no row of its own — it is the boundary a segment
  // ends at — so the current list is read off `manual_end_id`.
  const manualCheckpoints = useMemo(
    () => racePlanSegments
      .filter((seg) => seg.manual_end_id)
      .map((seg) => ({
        id: seg.manual_end_id,
        name: seg.end_label,
        distance_m: seg.end_distance_m,
      })),
    [racePlanSegments],
  );

  // Everything already in the plan, so the picker can show it and warn about a
  // placement that would leave an unmergeable sliver.
  //
  // Checkpoints and terrain boundaries are kept apart deliberately. An aid
  // station is a place the runner is deciding around; a climb top is just where
  // the gradient changed. Drawing them identically is what made the first
  // version unreadable — a long race turns into a picket fence of grey ticks.
  const planBoundaries = useMemo(() => {
    const out = [];
    racePlanSegments.forEach((seg) => {
      if (seg.segment_type === 'checkpoint') {
        out.push({
          distance_m: seg.start_distance_m,
          label: seg.start_label,
          kind: 'checkpoint',
        });
      } else if (seg.end_distance_m != null) {
        out.push({
          distance_m: seg.end_distance_m,
          label: seg.end_label,
          kind: seg.manual_end_id ? 'manual' : 'terrain',
        });
      }
    });
    return out.sort((a, b) => a.distance_m - b.distance_m);
  }, [racePlanSegments]);

  const persistManualCheckpoints = async (list) => {
    const { sessionId, predictionId } = watchPlanIds();
    if (predictionId) return setPredictionManualCheckpoints(predictionId, list);
    if (sessionId) return setAnalysisManualCheckpoints(sessionId, list);
    throw new Error('no session');
  };

  // Saving replaces the whole list, then rebuilds: the plan is derived, so the
  // grid only shows the checkpoint once the server has merged it in.
  const handleSaveCheckpoint = async (checkpoint, { addAnother } = {}) => {
    const next = checkpoint.id
      ? manualCheckpoints.map((mc) => (mc.id === checkpoint.id ? checkpoint : mc))
      : [...manualCheckpoints, checkpoint];
    await persistManualCheckpoints(next);
    const data = await buildRacePlanFn();
    loadRacePlanSegments(data.race_plan_segments);
    if (!addAnother) {
      setCheckpointDialogOpen(false);
      setEditingCheckpoint(null);
    }
  };

  const handleDeleteCheckpoint = async (manualId) => {
    setCheckpointBusy(true);
    try {
      await persistManualCheckpoints(manualCheckpoints.filter((mc) => mc.id !== manualId));
      const data = await buildRacePlanFn();
      loadRacePlanSegments(data.race_plan_segments);
    } finally {
      setCheckpointBusy(false);
    }
  };

  const openCheckpointDialog = (initial = null) => {
    setEditingCheckpoint(initial);
    setCheckpointDialogOpen(true);
  };

  // ── mkpaceLIVE watch plan ──────────────────────────────────────────────
  // Opening this must never invalidate a URL that is already in someone's watch
  // settings, so the button READS the existing link and only publishes when there
  // isn't one. Replacing it is a separate, confirmed action inside the dialog.
  const [watchPlanLoading, setWatchPlanLoading] = useState(false);
  const [watchPlanOpen, setWatchPlanOpen] = useState(false);
  const [watchPlanLink, setWatchPlanLink] = useState(null);
  const [watchPlanError, setWatchPlanError] = useState(null);
  // True while we are waiting on the race plan build rather than on the publish
  // itself. Worth its own flag: that build geocodes every climb/descent boundary
  // against OSM, which on a file from an area we have not seen before takes the
  // better part of a minute — long enough that an unexplained spinner reads as a
  // hang. The dialog says what is happening instead.
  const [watchPlanBuilding, setWatchPlanBuilding] = useState(false);
  const [watchPlanInfoAnchor, setWatchPlanInfoAnchor] = useState(null);

  // A finished prediction is the moment mkpaceLIVE becomes worth pressing, and
  // the button is a product name in a row of other buttons — nothing about it
  // says "this puts the plan on your watch". So it pulses once when a prediction
  // loads, the same pulse the race picker uses on the analysis before it.
  const [flashWatchBtn, setFlashWatchBtn] = useState(false);
  // Booleans, not the prop itself: buildRacePlanFn is a fresh closure on every
  // render, so depending on it would restart the timer forever.
  const canBuildPlan = !!buildRacePlanFn;
  const predictionSessionId = workoutData?.prediction_session_id || null;
  useEffect(() => {
    if (!canBuildPlan || !predictionSessionId) { setFlashWatchBtn(false); return; }
    setFlashWatchBtn(true);
    const id = setTimeout(() => setFlashWatchBtn(false), PULSE_TOTAL_MS);
    return () => clearTimeout(id);
  }, [canBuildPlan, predictionSessionId]);
  // On a phone the reanalyse controls (Snap to GPX, GPS corrections, Reanalyze) are
  // collapsed behind one "Advanced" icon — they are set-once options, not things you
  // reach for every visit, and inline they ate a third of the header.
  const [advancedAnchor, setAdvancedAnchor] = useState(null);

  const watchPlanIds = () => ({
    sessionId: workoutData?.analysis_session_id,
    predictionId: workoutData?.prediction_session_id,
  });

  /**
   * Make sure a race plan exists, and report the granularity that suits it.
   *
   * Keyed off "are there segments?" rather than "did we start a build?": a build
   * that was triggered and then failed, or returned nothing, left the old flag set
   * and the plan missing, so the next publish hit the backend's "Race plan not
   * built yet". Building is idempotent server-side (it returns the cached plan),
   * so asking again whenever segments are missing costs nothing.
   */
  /**
   * The build currently in flight, if any.
   *
   * Opening the Plan tab starts a build, and mkpaceLIVE needs the same plan. Those
   * used to be two independent requests: clicking the button while the tab's build
   * was still running fired a second one, so the slowest part (the OSM lookups) ran
   * twice over and the button raced the tab instead of waiting for it. Everything
   * now goes through startRacePlanBuild, so a second caller joins the promise that
   * is already running rather than starting another.
   */
  const racePlanBuildRef = useRef(null);

  const startRacePlanBuild = () => {
    if (racePlanBuildRef.current) return racePlanBuildRef.current;
    setRacePlanTriggered(true);
    setRacePlanLoading(true);
    const build = buildRacePlanFn()
      .then((data) => loadRacePlanSegments(data.race_plan_segments))
      .finally(() => {
        racePlanBuildRef.current = null;
        setRacePlanLoading(false);
      });
    racePlanBuildRef.current = build;
    return build;
  };

  const ensureRacePlan = async ({ force = false } = {}) => {
    if (!buildRacePlanFn) return planGranularity;
    // `force` is for the retry below: there the client HAS segments but the server
    // does not, which is the whole reason the publish failed — checking local state
    // again would skip the rebuild and fail identically.
    if (!force && racePlanSegments.length > 0) return planGranularity;
    return startRacePlanBuild();
  };

  /**
   * Turn a failed publish into something that says what actually went wrong.
   *
   * "Race plan not built yet" is the backend's wording for a state the user cannot
   * act on and did not cause — the plan is still being built — so it becomes a
   * plain description of that instead. An error with no detail at all means the
   * request never came back (the build can hold the connection open long enough
   * for a proxy to cut it), which is worth saying rather than blaming the user.
   */
  const watchPlanErrorText = (raw) => {
    // FastAPI answers a validation failure with `detail` as an ARRAY of objects.
    // Handing that to <Alert>{error}</Alert> crashes the render ("Objects are not
    // valid as a React child"), turning a bad request into a blank page, so only a
    // string is ever allowed to reach the dialog.
    const detail = typeof raw === 'string' ? raw : '';
    if (/not built/i.test(detail)) {
      return t(
        'watchPlanStillBuilding',
        'Your race plan is still being built. Give it a moment and press mkpaceLIVE again.',
      );
    }
    if (detail) return detail;
    return t(
      'watchPlanFailed',
      'Could not publish the plan — the server did not answer in time. This usually means the race plan is still being built; wait a moment and try again.',
    );
  };

  const handleOpenWatchPlan = async (granularityOverride, { allowBuild = true } = {}) => {
    const { sessionId, predictionId } = watchPlanIds();
    if (!sessionId && !predictionId) return;
    // Only ever accept a real granularity here. This is wired straight to an
    // onClick in the Plan tab, which hands the handler a click event as its first
    // argument — that event used to sail through as the granularity and get posted
    // as the request body, which axios cannot serialise (the event holds circular
    // DOM references). The publish then rejected with no response at all, so the
    // dialog had no detail to show and fell back to a generic "try again".
    const override = typeof granularityOverride === 'string' ? granularityOverride : null;
    const gran = override || planGranularity;
    setWatchPlanLoading(true);
    setWatchPlanError(null);
    setWatchPlanLink(null);
    setWatchPlanOpen(true);
    try {
      const existing = predictionId
        ? await getPredictionWatchPlan(predictionId)
        : await getAnalysisWatchPlan(sessionId);
      // Publish on first use only; afterwards just show what is already out there.
      const link = existing?.link || (predictionId
        ? await createPredictionWatchPlan(predictionId, gran)
        : await createAnalysisWatchPlan(sessionId, gran));
      setWatchPlanLink(link);
    } catch (err) {
      // Normalised to a string here (see watchPlanErrorText): a 422 answers with
      // `detail` as an array, which must never reach the dialog as-is.
      const rawDetail = err?.response?.data?.detail;
      const detail = typeof rawDetail === 'string' ? rawDetail : '';
      // The backend refuses to publish a plan it has not got. That can happen for
      // reasons this component cannot see (a build that silently failed, a plan
      // built against a different session id), so build and retry once rather
      // than telling the user to go and do it themselves.
      if (allowBuild && buildRacePlanFn && /not built/i.test(detail)) {
        try {
          setWatchPlanError(null);
          setWatchPlanBuilding(true);
          const built = await ensureRacePlan({ force: true });
          setWatchPlanBuilding(false);
          setWatchPlanLoading(false);
          return await handleOpenWatchPlan(built, { allowBuild: false });
        } catch {
          setWatchPlanBuilding(false);
          // fall through to the original error
        }
      }
      setWatchPlanError(watchPlanErrorText(detail));
    } finally {
      setWatchPlanLoading(false);
    }
  };

  // Header shortcut. The plan may not be built yet (the backend refuses to publish
  // one that isn't), so build it first and use the granularity that build chose —
  // reading planGranularity here would give the stale pre-build value. Also switches
  // to the Plan tab so the user can see what they just published.
  const handleHeaderMkpaceLive = async () => {
    if (racePlanTabIndex != null) setCurrentTab(racePlanTabIndex);

    let gran = planGranularity;
    if (buildRacePlanFn && racePlanSegments.length === 0) {
      // Open the dialog before waiting, not after. The build behind this can run
      // for the better part of a minute on a file from an area we have not
      // geocoded before, and doing it silently left the user staring at a button
      // spinner with nothing telling them the work was under way.
      setWatchPlanLink(null);
      setWatchPlanError(null);
      setWatchPlanBuilding(true);
      setWatchPlanOpen(true);
      try {
        gran = await ensureRacePlan();
      } catch {
        setWatchPlanBuilding(false);
        setWatchPlanError(
          t('watchPlanBuildFailed', 'Could not build the race plan. Open the Plan tab and try again.'),
        );
        return;
      }
      setWatchPlanBuilding(false);
    }
    await handleOpenWatchPlan(gran);
  };

  // Revoke the current token and mint a new one at `gran` — the deliberate push
  // of a changed plan. The new URL has to be re-pasted in Garmin Connect, which
  // is the only thing that makes the watch drop its cached copy.
  const handleRepublishWatchPlan = async (gran) => {
    const { sessionId, predictionId } = watchPlanIds();
    if (!sessionId && !predictionId) return;
    const link = predictionId
      ? await createPredictionWatchPlan(predictionId, gran, true)
      : await createAnalysisWatchPlan(sessionId, gran, true);
    setWatchPlanLink(link);
    return link;
  };

  useEffect(() => {
    if (!workoutData) return;

    setChunks(workoutData.chunks || []);
    setSummaryStats({
      total_distance: workoutData.total_distance,
      total_duration: workoutData.total_duration,
      total_moving_time: workoutData.total_moving_time,
      avg_pace: workoutData.avg_pace,
      avg_moving_pace: workoutData.avg_moving_pace,
      best_pace: workoutData.best_pace,
      avg_gap: workoutData.avg_gap,
      avg_heart_rate: workoutData.avg_heart_rate,
      max_heart_rate: workoutData.max_heart_rate,
      avg_cadence: workoutData.avg_cadence,
      total_elevation_gain: workoutData.total_elevation_gain,
      total_elevation_loss: workoutData.total_elevation_loss,
      curvature_score: workoutData.curvature_score,
      curvature_index: workoutData.curvature_index,
      stamina_score: workoutData.stamina_score,
      stamina_index: workoutData.stamina_index,
      total_paused_time: workoutData.total_paused_time,
      num_paused_chunks: workoutData.num_paused_chunks,
      ...deriveStopSplit(workoutData),
    });
    setRace(workoutData.race || null);
    setAnalyzedCheckpoints(workoutData.analyzed_checkpoints || []);
    setOriginalFitPoints(workoutData.original_fit_points || null);
    setClimbs(workoutData.climbs || []);
    setDescents(workoutData.descents || []);
    const rps = workoutData.race_plan_segments || [];
    loadRacePlanSegments(rps);
    // Reset trigger on every workoutData change; skip fetch only if plan already cached
    setRacePlanTriggered(rps.length > 0);

    // Compute moving-only summary averages from chunks
    const chks = workoutData.chunks || [];
    const nextSummary = {
      moving_avg_pace: null,
      moving_avg_gap: null,
      computed_avg_gap: null,
      moving_avg_hr: null,
      moving_avg_asc_vspeed: null,
      moving_avg_desc_vspeed: null,
    };

    if (chks.length > 0) {
      // Moving pace — from backend total
      if (workoutData.avg_moving_pace != null && workoutData.avg_moving_pace > 0) {
        nextSummary.moving_avg_pace = workoutData.avg_moving_pace;
      }

      // Moving Normalized Pace — distance-weighted avg of moving_gap.
      // Distance-weighting is consistent with moving_pace = total_time/distance and guarantees
      // moving_avg_gap ≤ moving_avg_pace (since gap_i ≤ pace_i per chunk by construction).
      // moving_gap covers only the moving part of a chunk, so it is weighted by
      // moving_distance to match — the same pairing the backend now uses, so the two
      // agree instead of differing by the standing-still distance. Predictions carry
      // no moving_distance and fall back to distance, which is the same thing there.
      const gapW = (c) => (c.moving_distance ?? c.distance) || 0;
      const gapChks = chks.filter(c => !c.is_paused && c.moving_gap > 0 && gapW(c) > 0);
      const gapDist = gapChks.reduce((s, c) => s + gapW(c), 0);
      if (gapDist > 0) {
        nextSummary.moving_avg_gap =
          gapChks.reduce((s, c) => s + c.moving_gap * gapW(c), 0) / gapDist;
      }

      // Avg Normalized Pace — moving norm pace scaled by stop-time penalty, matching how
      // avg_pace = moving_pace × (total_time / moving_time). Computed here from chunks so
      // predictions get the same treatment as real workouts; the backend now computes
      // avg_gap the same way, so this no longer overrides a different answer — it agrees
      // with it. Consumers that bypass this view (ComparisonSummary) read the backend
      // value and used to show the inflated one.
      const totalDur = workoutData.total_duration || workoutData.total_moving_time || 0;
      const movingDur = workoutData.total_moving_time || 0;
      if (nextSummary.moving_avg_gap > 0 && movingDur > 0 && totalDur > 0) {
        nextSummary.computed_avg_gap = nextSummary.moving_avg_gap * (totalDur / movingDur);
      }

      // Moving HR
      const hrChks = chks.filter(c => c.moving_heart_rate != null && c.moving_heart_rate > 0);
      if (hrChks.length > 0) {
        const hrDur = hrChks.reduce((s, c) => s + (c.duration || 0), 0);
        if (hrDur > 0) {
          nextSummary.moving_avg_hr =
            hrChks.reduce((s, c) => s + c.moving_heart_rate * (c.duration || 0), 0) / hrDur;
        }
      }

      // Ascending vertical speed (uphill chunks only)
      const ascChks = chks.filter(c =>
        (c.moving_asc_vertical_speed != null && c.moving_asc_vertical_speed > 0) ||
        (c.moving_asc_vertical_speed == null && c.moving_vertical_speed != null && c.moving_vertical_speed > 0)
      );
      if (ascChks.length > 0) {
        const ascMt = ascChks.reduce((s, c) => s + (c.moving_time || 0), 0);
        if (ascMt > 0) {
          nextSummary.moving_avg_asc_vspeed =
            ascChks.reduce((s, c) => {
              const v = c.moving_asc_vertical_speed ?? (c.moving_vertical_speed > 0 ? c.moving_vertical_speed : 0);
              return s + v * (c.moving_time || 0);
            }, 0) / ascMt;
        }
      }

      // Descending vertical speed (downhill chunks only)
      const descChks = chks.filter(c =>
        (c.moving_desc_vertical_speed != null && c.moving_desc_vertical_speed > 0) ||
        (c.moving_desc_vertical_speed == null && c.moving_vertical_speed != null && c.moving_vertical_speed < 0)
      );
      if (descChks.length > 0) {
        const descMt = descChks.reduce((s, c) => s + (c.moving_time || 0), 0);
        if (descMt > 0) {
          nextSummary.moving_avg_desc_vspeed =
            descChks.reduce((s, c) => {
              const v = c.moving_desc_vertical_speed ?? (c.moving_vertical_speed < 0 ? Math.abs(c.moving_vertical_speed) : 0);
              return s + v * (c.moving_time || 0);
            }, 0) / descMt;
        }
      }
    }

    setMovingSummary(nextSummary);
  }, [workoutData]);

  // ── Units listener ─────────────────────────────────────────────────────
  useEffect(() => {
    const onUnitsChanged = (e) => setUnits(e.detail);
    window.addEventListener('unitsChanged', onUnitsChanged);
    return () => window.removeEventListener('unitsChanged', onUnitsChanged);
  }, []);

  // ── GPX route points ───────────────────────────────────────────────────
  useEffect(() => {
    if (!showGpxRoute || !race?.id) {
      setGpxRoutePoints(null);
      return;
    }
    let cancelled = false;
    getRunningRaceGpxPoints(race.id)
      .then(data => { if (!cancelled) setGpxRoutePoints(data.points || data); })
      .catch(err => console.error('Failed to fetch GPX route points:', err));
    return () => { cancelled = true; };
  }, [showGpxRoute, race]);

  // ── Handlers ───────────────────────────────────────────────────────────
  // The Plan tab needs chunks, nothing more: a course with no detected climbs or
  // descents still has a plan — it just collapses to a single Start→Finish row
  // (plus any checkpoints), which is exactly what a flat road race wants.
  const hasRacePlanTab = chunks.length > 0;
  const racePlanTabIndex = hasRacePlanTab ? 3 : undefined;
  const hasCheckpointStops = racePlanSegments.some((s) => s.segment_type === 'checkpoint');

  const displayedSegments = useMemo(
    () => (planGranularity === 'minimal' ? buildMinimalRacePlan(racePlanSegments) : racePlanSegments),
    [planGranularity, racePlanSegments]
  );

  // Markers for the Plan Map, from the same helper that drives the altitude
  // chart's boundary lines — the two views must mark the same places. Declared
  // after displayedSegments, which it reads.
  const planMapMarkers = useMemo(
    () => getPlanBoundaryMarkers(displayedSegments, analyzedCheckpoints),
    [displayedSegments, analyzedCheckpoints],
  );

  const handleTabChange = (_, newValue) => {
    setCurrentTab(newValue);
    if (newValue === racePlanTabIndex && !racePlanTriggered && buildRacePlanFn) {
      // Shared with mkpaceLIVE — see startRacePlanBuild. Pressing that button while
      // this build is running joins it instead of starting a competing one.
      startRacePlanBuild().catch(err => console.error('Failed to build race plan:', err));
    }
  };

  const handleClimbClick = (range) => setActiveClimbRange(range);
  const handleClimbDoubleClick = (range) => {
    setActiveClimbRange(range);
    setClimbZoom(range);
  };
  const handleDescentClick = (range) => setActiveDescentRange(range);
  const handleDescentDoubleClick = (range) => {
    setActiveDescentRange(range);
    setDescentZoom(range);
  };

  // Status notices surfaced as icon buttons under the file info (recording mode, distance
  // correction). Each carries a severity + the same text that used to render as a full-width Alert.
  const recordingNotice = (workoutData.recording_mode && workoutData.recording_mode !== '1s') ? {
    severity: 'warning',
    text: workoutData.recording_mode === 'ultratrac'
      ? t('ultratracWarning', { interval: workoutData.median_recording_interval, defaultValue: `This file was recorded in UltraTrac/Expedition mode (median interval: ${workoutData.median_recording_interval}s). Distance and pace data may be significantly less accurate.` })
      : t('smartRecordingWarning', { interval: workoutData.median_recording_interval, defaultValue: `This file uses smart recording (median interval: ${workoutData.median_recording_interval}s). Distance and pace data may be less accurate than 1-second recording.` }),
  } : null;
  const _san = workoutData.sanitization;
  const distanceNotice = (_san && _san.orig_total_dist) ? {
    severity: _san.user_disabled ? 'warning' : (_san.applied ? 'info' : 'success'),
    text: _san.user_disabled
      ? t('sanitizationDisabled', { orig: (_san.orig_total_dist / 1000).toFixed(2), san: (_san.san_total_dist / 1000).toFixed(2), pct: _san.dist_pct.toFixed(1), defaultValue: `GPS corrections disabled by user. Anomalies detected but not corrected: watch recorded ${(_san.orig_total_dist / 1000).toFixed(2)} km, would have corrected to ${(_san.san_total_dist / 1000).toFixed(2)} km (${_san.dist_pct.toFixed(1)}%).` })
      : (_san.applied
        ? t('sanitizationApplied', { orig: (_san.orig_total_dist / 1000).toFixed(2), san: (_san.san_total_dist / 1000).toFixed(2), pct: _san.dist_pct.toFixed(1), defaultValue: `Distance correction applied: watch recorded ${(_san.orig_total_dist / 1000).toFixed(2)} km, corrected to ${(_san.san_total_dist / 1000).toFixed(2)} km (${_san.dist_pct.toFixed(1)}%). GPS speed anomalies were detected and corrected.` })
        : t('sanitizationNotNeeded', { pct: _san.dist_pct.toFixed(1), defaultValue: `GPS corrections not necessary. Distance change is within acceptable limits (${_san.dist_pct.toFixed(1)}%).` })),
  } : null;

  // ── Render ─────────────────────────────────────────────────────────────
  // mkpaceLIVE first, then whatever the page contributes (Compare / Share /
  // Dashboard). Rendered inside the header row on desktop and as its own full-width
  // row on mobile, so the markup lives in one place either way.
  const actionButtons = (headerActions || buildRacePlanFn) ? (
    <Box
      display="flex"
      flexWrap="wrap"
      alignItems="center"
      gap={1}
      mb={isMobile ? 2 : 0}
      sx={{ width: isMobile ? '100%' : 'auto', justifyContent: 'flex-end' }}
    >
            {buildRacePlanFn && (
              <Button
                variant="outlined"
                startIcon={watchPlanLoading ? <CircularProgress size={14} /> : <WatchIcon />}
                onClick={() => { setFlashWatchBtn(false); handleHeaderMkpaceLive(); }}
                disabled={
                  watchPlanLoading ||
                  watchPlanBuilding ||
                  (!workoutData?.analysis_session_id && !workoutData?.prediction_session_id)
                }
                sx={{
                  color: colors.grey[100],
                  borderColor: colors.blueAccent[600],
                  textTransform: 'none',
                  minWidth: isMobile ? 'auto' : undefined,
                  px: isMobile ? 2 : undefined,
                  fontSize: isMobile ? '0.75rem' : undefined,
                  '&:hover': { borderColor: colors.blueAccent[400] },
                  ...(flashWatchBtn ? {
                    '@keyframes watchBtnFlash': {
                      '0%, 100%': {
                        borderColor: colors.blueAccent[600],
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                      },
                      '50%': {
                        borderColor: pulse.edge,
                        backgroundColor: pulse.fill,
                        boxShadow: `0 0 0 4px ${pulse.glow}`,
                      },
                    },
                    animation: pulseAnimation('watchBtnFlash'),
                    '@media (prefers-reduced-motion: reduce)': {
                      animation: 'none',
                      borderColor: pulse.edge,
                      boxShadow: `0 0 0 3px ${pulse.glow}`,
                    },
                  } : {}),
                }}
              >
                {t('watchPlanButton', 'mkpaceLIVE')}
              </Button>
            )}
            {headerActions}
      {!isMobile && headerNav}
    </Box>
  ) : null;

  return (
    <Box m={isMobile ? '2px' : '20px'} display="flex" justifyContent="center">
      <Box maxWidth="1200px" width="100%">

        {/* Header */}
        <Box
          mb={4}
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          gap={2}
          sx={{ width: '100%', overflow: 'hidden' }}
        >
          <Box sx={{ width: isMobile ? '100%' : 'auto', overflow: 'hidden' }}>
            {headerLeft}
            {(recordingNotice || distanceNotice || headerRight || (isMobile && headerNav)) && (
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                mt={0.5}
                sx={{ width: isMobile ? '100%' : 'auto' }}
              >
                {recordingNotice && (
                  <Tooltip title={t('recordingModeNotice', 'Recording mode notice')}>
                    <IconButton
                      size="small"
                      onClick={(e) => setRecNoticeAnchor(e.currentTarget)}
                      sx={{ color: theme.palette.warning.main }}
                    >
                      <FiberSmartRecordIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {distanceNotice && (
                  <Tooltip title={t('distanceCorrectionNotice', 'Distance correction notice')}>
                    <IconButton
                      size="small"
                      onClick={(e) => setDistNoticeAnchor(e.currentTarget)}
                      sx={{ color: theme.palette[distanceNotice.severity].main }}
                    >
                      <StraightenIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                {headerRight && (
                  <Tooltip title={t('advancedOptions', 'Advanced')}>
                    <Button
                      size="small"
                      startIcon={<TuneIcon fontSize="small" />}
                      onClick={(e) => setAdvancedAnchor(e.currentTarget)}
                      sx={{
                        color: colors.grey[300],
                        textTransform: 'none',
                        fontSize: '0.75rem',
                      }}
                    >
                      {t('advancedOptions', 'Advanced')}
                    </Button>
                  </Tooltip>
                )}

                {/* Phone only: parking it here leaves mkpaceLIVE / Compare / Share on
                    one line below instead of wrapping onto two. */}
                {isMobile && headerNav && (
                  <Box sx={{ ml: 'auto' }}>{headerNav}</Box>
                )}
              </Box>
            )}
          </Box>
          {/* Desktop: the freed space takes the action buttons, so the header is one
              row instead of two. On a phone they stay full-width below. */}
          {!isMobile && actionButtons}
        </Box>

        {/* Mobile: the same controls, one tap away instead of always on screen. */}
        <Popover
          open={Boolean(advancedAnchor)}
          anchorEl={advancedAnchor}
          onClose={() => setAdvancedAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box
            sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5, minWidth: 240 }}
            onClick={(e) => {
              // Close once an action is taken — a checkbox toggle should leave the
              // popover open, but Reanalyze reloads the view behind it.
              if (e.target.closest('button')?.type !== 'button') return;
              if (/reanalyz|back to actual/i.test(e.target.closest('button')?.textContent || '')) {
                setAdvancedAnchor(null);
              }
            }}
          >
            {headerRight}
          </Box>
        </Popover>

        {isMobile && actionButtons}

        {/* Root-level so it opens from the header shortcut as well as the Plan tab. */}
        {buildRacePlanFn && (
          <ManualCheckpointDialog
            open={checkpointDialogOpen}
            onClose={() => { setCheckpointDialogOpen(false); setEditingCheckpoint(null); }}
            onSave={handleSaveCheckpoint}
            sessionId={workoutData?.analysis_session_id}
            chunks={chunks}
            totalDistanceM={workoutData?.total_distance}
            existingBoundaries={planBoundaries}
            initial={editingCheckpoint}
          />
        )}

        <WatchPlanDialog
          open={watchPlanOpen}
          onClose={() => setWatchPlanOpen(false)}
          link={watchPlanLink}
          error={watchPlanError}
          building={watchPlanBuilding}
          granularity={planGranularity}
          chunks={chunks}
          segments={racePlanSegments}
          onRepublish={handleRepublishWatchPlan}
          onUnpublished={() => setWatchPlanLink(null)}
        />

        {/* Below-header slot (e.g. race prediction bar) */}
        {belowHeader}

        {/* Recording-mode + distance-correction notices are surfaced as icon buttons under the
            file info (see header) and shown in these popovers when clicked. */}
        <Popover
          open={!!recNoticeAnchor}
          anchorEl={recNoticeAnchor}
          onClose={() => setRecNoticeAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          {recordingNotice && (
            <Alert severity={recordingNotice.severity} sx={{ m: 0, maxWidth: 420 }}>
              {recordingNotice.text}
            </Alert>
          )}
        </Popover>
        <Popover
          open={!!distNoticeAnchor}
          anchorEl={distNoticeAnchor}
          onClose={() => setDistNoticeAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          {distanceNotice && (
            <Alert severity={distanceNotice.severity} sx={{ m: 0, maxWidth: 420 }}>
              {distanceNotice.text}
            </Alert>
          )}
        </Popover>

        {/* Sparse GPS end-trim notice */}
        {workoutData.sanitization?.sparse_gps_trim && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('sparseGpsTrim', { count: workoutData.sanitization.sparse_gps_trim.removed_records, mins: Math.round(workoutData.sanitization.sparse_gps_trim.trimmed_seconds / 60), defaultValue: `Post-race recording trimmed: ${workoutData.sanitization.sparse_gps_trim.removed_records} sparse GPS record(s) covering ${Math.round(workoutData.sanitization.sparse_gps_trim.trimmed_seconds / 60)} min were removed from the end of the file. Duration shown reflects actual activity time.` })}
          </Alert>
        )}

        {/* Snap to GPX notice */}
        {workoutData.snap_to_gpx_result && (
          <Alert
            severity={workoutData.snap_to_gpx_result.skipped ? 'warning' : 'info'}
            sx={{ mb: 2 }}
          >
            {workoutData.snap_to_gpx_result.skipped ? (
              t('snapToGpxSkipped', { on_course: workoutData.snap_to_gpx_result.on_course_points || 0, total: workoutData.snap_to_gpx_result.total_points, defaultValue: `Snap to GPX was skipped: only ${workoutData.snap_to_gpx_result.on_course_points || 0}/${workoutData.snap_to_gpx_result.total_points} points were detected on-course (<10% threshold). GPS track too far from race route.` })
            ) : (
              t('snapToGpxApplied', { snapped: workoutData.snap_to_gpx_result.snapped_points, total: workoutData.snap_to_gpx_result.total_points, avg_drift: workoutData.snap_to_gpx_result.avg_drift_m.toFixed(1), detours: workoutData.snap_to_gpx_result.detour_sections, defaultValue: `Snap to GPX applied: ${workoutData.snap_to_gpx_result.snapped_points}/${workoutData.snap_to_gpx_result.total_points} points corrected (avg drift: ${workoutData.snap_to_gpx_result.avg_drift_m.toFixed(1)}m). ${workoutData.snap_to_gpx_result.detour_sections} detour section(s) preserved.` })
            )}
          </Alert>
        )}

        {/* ── Summary Stats ─────────────────────────────────────────────── */}
        <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: isMobile ? 2 : 4 }}>

          {/* Row 1: Distance · Duration · Elevation Gain · Elevation Loss · Stopping Time */}
          <Grid size={{ xs: 6, md: 2.4 }}>
            <StatBox
              title={t('distance', 'Distance')}
              value={formatDistance(summaryStats.total_distance, units)}
              icon={<DirectionsRunIcon sx={{ fontSize: '32px' }} />}
              iconColor={colors.chartColors[12]}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 2.4 }}>
            <StatBox
              title={t('duration', 'Duration')}
              value={formatDuration(summaryStats.total_duration)}
              icon={<TimerIcon sx={{ fontSize: '32px' }} />}
              iconColor={colors.chartColors[0]}
            />
          </Grid>
          {summaryStats.total_moving_time != null && summaryStats.total_moving_time > 0 && (
            <Grid size={{ xs: 6, md: 2.4 }}>
              <StatBox
                title={t('movingTime', 'Moving Time')}
                value={formatDuration(summaryStats.total_moving_time)}
                subtitle={
                  summaryStats.checkpoint_stop_time_s != null
                    ? `${t('stopsLabel', 'Stops')}: ${formatShortStop(summaryStats.checkpoint_stop_time_s)} + ${formatShortStop(summaryStats.free_stop_time_s || 0)}`
                    : summaryStats.total_stop_time_s != null
                    ? `${t('stopsLabel', 'Stops')}: ${formatShortStop(summaryStats.total_stop_time_s)}`
                    : `${summaryStats.num_paused_chunks || 0} ${t('stops', 'stops')}`
                }
                icon={<PlayArrowIcon sx={{ fontSize: '32px' }} />}
                iconColor={colors.warnings.orange}
                infoContent={
                  (summaryStats.checkpoint_stop_time_s != null || summaryStats.total_stop_time_s != null) ? (
                    <>
                      <Typography variant="subtitle2" gutterBottom>{t('movingTimeInfoTitle', 'Moving Time')}</Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('movingTimeInfo1', 'The time you are actually running — all stopped time is excluded.')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('movingTimeInfo2', 'Below it, "Stops" is your total stopped time. On races with aid stations it is split in two: time spent AT checkpoints, plus scattered stops between them (gear, photos, nature breaks, micro-stops on steep ground).')}
                      </Typography>
                    </>
                  ) : undefined
                }
              />
            </Grid>
          )}
          <Grid size={{ xs: 6, md: 2.4 }}>
            <StatBox
              title={t('elevationGain', 'Elevation Gain')}
              value={formatElevation(summaryStats.total_elevation_gain, units)}
              icon={<TerrainIcon sx={{ fontSize: '32px' }} />}
              iconColor={`color-mix(in srgb, ${colors.chartColors[1]} 70%, black)`}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 2.4 }}>
            <StatBox
              title={t('elevationLoss', 'Elevation Loss')}
              value={formatElevation(summaryStats.total_elevation_loss, units)}
              icon={<TerrainIcon sx={{ fontSize: '32px' }} />}
              iconColor={colors.chartColors[1]}
            />
          </Grid>

          {/* Row 2: Avg Pace · Moving Pace · Avg Norm. Pace · Moving Norm. Pace · Avg HR */}
          <Grid size={{ xs: 6, md: 2.4 }}>
            <StatBox
              title={t('avgPace', 'Avg Pace')}
              value={formatPace(summaryStats.avg_pace, units)}
              icon={<SpeedIcon sx={{ fontSize: '32px' }} />}
              iconColor={colors.chartColors[2]}
            />
          </Grid>
          {movingSummary.moving_avg_pace != null && (
            <Grid size={{ xs: 6, md: 2.4 }}>
              <StatBox
                title={t('movingPace', 'Moving Pace')}
                value={formatPace(movingSummary.moving_avg_pace, units)}
                icon={<SpeedIcon sx={{ fontSize: '32px' }} />}
                iconColor={colors.chartColors[3]}
              />
            </Grid>
          )}
          <Grid size={{ xs: 6, md: 2.4 }}>
            <StatBox
              title={t('avgGAP', 'Avg Norm. Pace')}
              value={formatPace(movingSummary.computed_avg_gap ?? summaryStats.avg_gap, units)}
              icon={<TerrainIcon sx={{ fontSize: '32px' }} />}
              iconColor={colors.chartColors[4]}
              infoContent={
                <>
                  <Typography variant="subtitle2" gutterBottom>{t('avgGapInfoTitle', 'Avg Normalized Pace (GAP)')}</Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('avgGapInfo1', 'GAP — Grade Adjusted Pace — is your pace corrected for elevation. Climbs and descents are converted to their flat-road equivalent so you can compare efforts across different terrains.')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('avgGapInfo2', 'Includes time spent stopped. A GAP of 6:30/km on a hilly course means you were working as hard as if you ran 6:30/km on flat ground the whole way.')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('avgGapInfo3', 'The Stamina score is also based on GAP — it tracks whether your GAP held steady or drifted as the race went on.')}
                  </Typography>
                </>
              }
            />
          </Grid>
          {movingSummary.moving_avg_gap != null && (
            <Grid size={{ xs: 6, md: 2.4 }}>
              <StatBox
                title={t('movingGAP', 'Moving Norm. Pace')}
                value={formatPace(movingSummary.moving_avg_gap, units)}
                icon={<TerrainIcon sx={{ fontSize: '32px' }} />}
                iconColor={colors.chartColors[5]}
                infoContent={
                  <>
                    <Typography variant="subtitle2" gutterBottom>{t('movingGapInfoTitle', 'Moving Normalized Pace (Moving GAP)')}</Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('movingGapInfo1', 'Same as Avg GAP but with stopped time excluded — only the moments you were actually moving count.')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('movingGapInfo2', "This is the most accurate reflection of your running effort. If you stopped for 20 minutes at an aid station, those minutes don't drag the number down.")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('movingGapInfo3', 'The Stamina score uses this moving GAP to measure fade — comparing your GAP in the final stretch to your GAP at the start.')}
                    </Typography>
                  </>
                }
              />
            </Grid>
          )}
          <Grid size={{ xs: 6, md: 2.4 }}>
            <StatBox
              title={t('avgHR', 'Avg HR')}
              value={summaryStats.avg_heart_rate ? formatHeartRate(summaryStats.avg_heart_rate) : '0 bpm'}
              icon={<FavoriteIcon sx={{ fontSize: '32px' }} />}
              iconColor={colors.chartColors[6]}
            />
          </Grid>

          {/* Row 3: Moving Asc V.Spd · Moving Desc V.Spd · Stamina */}
          {movingSummary.moving_avg_asc_vspeed != null && (
            <Grid size={{ xs: 6, md: 4 }}>
              <StatBox
                title={t('movingAscVSpeed', 'Moving Asc V.Spd')}
                value={formatVerticalSpeed(movingSummary.moving_avg_asc_vspeed, units)}
                icon={<TerrainIcon sx={{ fontSize: '32px' }} />}
                iconColor={colors.greenAccent[500]}
              />
            </Grid>
          )}
          {movingSummary.moving_avg_desc_vspeed != null && (
            <Grid size={{ xs: 6, md: 4 }}>
              <StatBox
                title={t('movingDescVSpeed', 'Moving Desc V.Spd')}
                value={formatVerticalSpeed(movingSummary.moving_avg_desc_vspeed, units)}
                icon={<TerrainIcon sx={{ fontSize: '32px' }} />}
                iconColor={colors.blueAccent[500]}
              />
            </Grid>
          )}
          <Grid size={{ xs: 6, md: 4 }}>
            <StatBox
              title={t('overallStamina', 'Overall Stamina')}
              value={summaryStats.stamina_score != null ? `${summaryStats.stamina_score.toFixed(1)}%` : '0%'}
              subtitle={summaryStats.stamina_index != null ? `${summaryStats.stamina_index}/5` : '0/5'}
              icon={<BatteryChargingFullIcon sx={{ fontSize: '32px' }} />}
              iconColor={
                summaryStats.stamina_index === 5 ? colors.greenAccent[500] :
                summaryStats.stamina_index === 4 ? colors.greenAccent[600] :
                summaryStats.stamina_index === 3 ? colors.warnings.orange :
                summaryStats.stamina_index != null ? colors.redAccent[500] :
                colors.grey[600]
              }
              infoContent={
                <>
                  <Typography variant="subtitle2" gutterBottom>{t('staminaInfoTitle', 'Overall Stamina')}</Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('staminaInfo1', 'How well you held your pace from start to finish. It compares your grade-adjusted pace (GAP) at the end of the run to your pace at the beginning.')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('staminaInfo2', '100% means you finished as strong as you started. Below 100% means you slowed down — the lower the number, the more you faded.')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('staminaInfo3', 'The 1–5 index groups this into levels: 5 = excellent, held pace throughout. 3 = noticeable fade in the second half. 1 = significant slowdown, likely hit a wall.')}
                  </Typography>
                </>
              }
            />
          </Grid>
        </Grid>

        {/* ── Tabs ──────────────────────────────────────────────────────── */}
        <Paper elevation={3} sx={{ backgroundColor: colors.grey[800], borderRadius: '8px', overflow: 'hidden' }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            sx={{
              backgroundColor: colors.grey[900],
              '& .MuiTab-root': {
                color: colors.grey[300],
                '&.Mui-selected': { color: colors.blueAccent[500] },
              },
            }}
          >
            <Tab label={t('overview', 'Overview')} />
            <Tab label={t('climbs', 'Climbs')} />
            <Tab label={t('descents', 'Descents')} />
            {hasRacePlanTab && <Tab label={t('racePlan', 'Plan')} />}

          </Tabs>

          {/* Lighter top padding — the tab strip already separates this from the header, so a full
              p=3 above the first row just wastes vertical space. */}
          <Box px={isMobile ? 0.5 : 3} pt={isMobile ? 0.5 : 1.5} pb={isMobile ? 0.5 : 3}>

            {/* Overview Tab */}
            {currentTab === 0 && (
              <Box>
                {chunks.length > 0 && (
                  <Box mb={3}>
                    {/* The GPX toggle rides in the map's own header row rather than a dedicated
                        line above it — see RunningRouteMap's headerExtra slot. */}
                    <RunningRouteMap
                      chunks={chunks}
                      race={race}
                      matchedCheckpoints={analyzedCheckpoints}
                      visibleDistanceRange={chartDistZoom}
                      gpxRoutePoints={showGpxRoute ? gpxRoutePoints : null}
                      originalFitPoints={originalFitPoints}
                      headerExtra={race && race.gpx_data ? (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={showGpxRoute}
                              onChange={(e) => setShowGpxRoute(e.target.checked)}
                              size="small"
                            />
                          }
                          label={<Typography variant="body2" color={colors.grey[300]}>{t('showGpxCourse', 'Show GPX course')}</Typography>}
                          sx={{ mr: 0 }}
                        />
                      ) : null}
                    />
                  </Box>
                )}

                <RunningWorkoutChartsSection
                  chunks={chunks}
                  chartType="distance"
                  race={race}
                  matchedCheckpoints={analyzedCheckpoints}
                  externalDistZoom={checkpointZoom}
                  onDistZoomChange={setChartDistZoom}
                />

                {analyzedCheckpoints.length > 0 && (
                  <Box mt={8}>
                    <RunningCheckpointsGrid
                      race={race}
                      checkpoints={analyzedCheckpoints}
                      workoutStartTime={workoutData.workout_start_time}
                      onCheckpointDoubleClick={setCheckpointZoom}
                      title={race ? t('raceCheckpoints', 'Race Checkpoints') : t('detectedStops', 'Detected Stops')}
                    />
                  </Box>
                )}
              </Box>
            )}

            {/* Climbs Tab */}
            {currentTab === 1 && (
              <Box>
                <Box mb={4}>
                  <RunningRouteMap
                    chunks={chunks}
                    race={race}
                    matchedCheckpoints={analyzedCheckpoints}
                    title={t('climbMapTitle', 'Climb Map')}
                    visibleDistanceRange={chartDistZoom}
                    climbHighlights={climbs}
                  />
                </Box>

                <RunningWorkoutChartsSection
                  chunks={chunks}
                  chartType="distance"
                  race={race}
                  matchedCheckpoints={analyzedCheckpoints}
                  externalDistZoom={climbZoom}
                  onDistZoomChange={setChartDistZoom}
                  climbs={climbs}
                  activeClimbRange={activeClimbRange}
                  defaultMetrics={['altitude', 'moving_asc_vertical_speed']}
                />

                {climbs.length > 0 && (
                  <Box mt={8}>
                    <RunningClimbsGrid
                      climbs={climbs}
                      title={t('detectedClimbs', 'Detected Climbs')}
                      onClimbClick={handleClimbClick}
                      onClimbDoubleClick={handleClimbDoubleClick}
                    />
                  </Box>
                )}
              </Box>
            )}

            {/* Race Plan Tab */}
            {currentTab === 3 && hasRacePlanTab && (
              <Box>
                {racePlanLoading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                    <CircularProgress />
                    <Typography ml={2} color={colors.grey[300]}>{t('buildingRacePlan', 'Building race plan...')}</Typography>
                  </Box>
                ) : racePlanSegments.length > 0 ? (
                  <>
                    <Box mb={4}>
                      <RunningRouteMap
                        chunks={chunks}
                        race={race}
                        matchedCheckpoints={analyzedCheckpoints}
                        planBoundaryMarkers={planMapMarkers}
                        title={t('planMapTitle', 'Plan Map')}
                        segmentHighlight={segmentHighlight}
                      />
                    </Box>
                    <RunningWorkoutChartsSection
                      chunks={chunks}
                      chartType="distance"
                      race={race}
                      matchedCheckpoints={analyzedCheckpoints}
                      externalDistZoom={segmentZoom}
                      onDistZoomChange={() => {}}
                      climbs={[]}
                      descents={[]}
                      activeSegmentRange={segmentHighlight}
                      racePlanSegments={displayedSegments}
                    />
                    <Box mt={4}>
                      {/* Action row: plan granularity on the left, exports on the right.
                          The toggle lives here because it decides what the exports —
                          the watch plan especially — actually contain. */}
                      <Box
                        display="flex"
                        flexWrap="wrap"
                        justifyContent="flex-end"
                        alignItems="center"
                        gap={1}
                        mb={1}
                      >
                        {hasCheckpointStops && (
                          <Box display="flex" justifyContent="flex-end" alignItems="center" gap={0.5}>
                            <ToggleButtonGroup
                              value={planGranularity}
                              exclusive
                              onChange={(_, v) => { if (v != null) setPlanGranularity(v); }}
                              size="small"
                            >
                              <ToggleButton
                                value="detailed"
                                sx={{
                                  color: colors.grey[400],
                                  borderColor: colors.grey[600],
                                  fontSize: '0.75rem',
                                  px: 1.5,
                                  '&.Mui-selected': { color: colors.blueAccent[300], backgroundColor: colors.grey[700] },
                                }}
                              >
                                {t('planDetailed', 'Detailed')}
                              </ToggleButton>
                              <ToggleButton
                                value="minimal"
                                sx={{
                                  color: colors.grey[400],
                                  borderColor: colors.grey[600],
                                  fontSize: '0.75rem',
                                  px: 1.5,
                                  '&.Mui-selected': { color: colors.blueAccent[300], backgroundColor: colors.grey[700] },
                                }}
                              >
                                {t('planMinimal', 'Minimal')}
                              </ToggleButton>
                            </ToggleButtonGroup>
                            <IconButton size="small" onClick={(e) => setPlanGranularityInfoAnchor(e.currentTarget)} sx={{ color: 'text.secondary', p: 0 }}>
                              <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                            <Popover
                              open={Boolean(planGranularityInfoAnchor)}
                              anchorEl={planGranularityInfoAnchor}
                              onClose={() => setPlanGranularityInfoAnchor(null)}
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                              <Box sx={{ p: 2, maxWidth: 320 }}>
                                <Typography variant="subtitle2" gutterBottom>{t('planGranularityInfoTitle', 'Detailed vs Minimal plan')}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {t('planGranularityInfoDesc', 'Detailed breaks the route into every climb, descent and rolling section. Minimal collapses each of those into a single row between checkpoints, keeping only the checkpoint stops — easier to read on a phone or watch.')}
                                </Typography>
                              </Box>
                            </Popover>
                          </Box>
                        )}
                        <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} ml="auto">
                        {/* Hidden for now: mkpaceLIVE supersedes both. The .fit workout carries pace
                            targets but no course, and the GPX carries the course but only waypoint
                            alerts; mkpaceLIVE sends the whole plan — pace, checkpoints, cutoffs and
                            elevation — and tracks it live. Flip the flag to bring them back; the
                            handlers, popovers and backend endpoints are all left intact. */}
                        {SHOW_LEGACY_WATCH_EXPORTS && (
                          <>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={planZwoLoading ? <CircularProgress size={14} /> : null}
                              onClick={handleDownloadPlanFit}
                              disabled={planZwoLoading || (!workoutData?.analysis_session_id && !workoutData?.prediction_session_id)}
                              sx={{ fontSize: '0.75rem' }}
                            >
                              {t('garminWorkout', 'Garmin Workout')}
                            </Button>
                            <IconButton size="small" onClick={(e) => setPlanZwoInfoAnchor(e.currentTarget)} sx={{ color: 'text.secondary', p: 0, mb: 1 }}>
                              <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                            <Popover
                              open={Boolean(planZwoInfoAnchor)}
                              anchorEl={planZwoInfoAnchor}
                              onClose={() => setPlanZwoInfoAnchor(null)}
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            >
                              <Box sx={{ p: 2, maxWidth: 340 }}>
                                <Typography variant="subtitle2" gutterBottom>{t('garminWorkoutInfoTitle', 'Garmin Workout — how to install')}</Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  {t('garminWorkoutInfoDesc', 'Downloads a .fit structured workout built from your race plan segments. Each segment becomes a step with a pace target on your Garmin watch.')}
                                </Typography>
                                <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>{t('garminWorkoutUsbTitle', 'Installing via USB (Garmin only)')}</Typography>
                                <Typography variant="body2" color="text.secondary" component="div">
                                  <ol style={{ margin: 0, paddingLeft: 18 }}>
                                    <li>{t('garminWorkoutStep1', 'Connect your Garmin watch to your computer via USB.')}</li>
                                    <li>Open the watch as a USB drive and go to the <strong>GARMIN / NEWFILES</strong> folder.</li>
                                    <li>{t('garminWorkoutStep3', 'Copy the .fit file into that folder.')}</li>
                                    <li>{t('garminWorkoutStep4', 'Safely eject the watch — it will import the workout automatically.')}</li>
                                    <li>On the watch: <strong>Training → Workouts → [race name]</strong>.</li>
                                  </ol>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                  {t('garminWorkoutNote1', 'The watch will guide you through each segment and alert you when the pace target changes.')}
                                </Typography>
                                <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                                  <strong>Important:</strong> {t('garminWorkoutWarning', 'the threshold pace in your mkpace profile must be accurate — it is used to calculate all pace targets in the workout.')}
                                </Typography>
                              </Box>
                            </Popover>
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.5} ml={2}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={watchGpxLoading ? <CircularProgress size={14} /> : <WatchIcon />}
                              onClick={handleDownloadWatchGpx}
                              disabled={watchGpxLoading || (!workoutData?.analysis_session_id && !workoutData?.prediction_session_id)}
                              sx={{ fontSize: '0.75rem' }}
                            >
                              {t('watchGpx', 'Watch GPX')}
                            </Button>
                            <IconButton size="small" onClick={(e) => setWatchGpxInfoAnchor(e.currentTarget)} sx={{ color: 'text.secondary', p: 0, mb: 1 }}>
                              <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                            <Popover
                              open={Boolean(watchGpxInfoAnchor)}
                              anchorEl={watchGpxInfoAnchor}
                              onClose={() => setWatchGpxInfoAnchor(null)}
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            >
                              <Box sx={{ p: 2, maxWidth: 340 }}>
                                <Typography variant="subtitle2" gutterBottom>{t('watchGpxInfoTitle', 'Watch GPX — how to use')}</Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  {t('watchGpxInfoDesc', 'Downloads a GPX course file with pace reminders for your GPS watch. Each segment boundary triggers a vibration alert showing your cumulative time target.')}
                                </Typography>
                                <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>{t('watchGpxGarminTitle', 'Garmin setup')}</Typography>
                                <Typography variant="body2" color="text.secondary" component="div">
                                  <ol style={{ margin: 0, paddingLeft: 18 }}>
                                    <li>Download the GPX file and go to <strong>Garmin Connect web → Training → Courses → Import</strong>.</li>
                                    <li>Open the <strong>Garmin Connect mobile app</strong> — the course syncs to your watch automatically.</li>
                                    <li>{t('watchGpxStep3', 'On the watch: Navigation → Courses → [race name] → Do Course.')}</li>
                                  </ol>
                                </Typography>
                                <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>{t('watchGpxDuringRaceTitle', 'During the race')}</Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  {t('watchGpxDuringRaceDesc', 'At each segment start the watch vibrates and shows a message like:')}
                                </Typography>
                                <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 1.5, py: 1, fontFamily: 'monospace', fontSize: '0.78rem', mb: 1 }}>
                                  KM12.3→52:30
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  {t('watchGpxExplain', "This means: by kilometer 12.3 your elapsed time should be 52:30. Cross-check against your watch's elapsed time to know if you're on pace.")}
                                </Typography>
                              </Box>
                            </Popover>
                          </Box>
                          </>
                        )}

                        {/* mkpaceLIVE — publish this plan to a URL the watch app fetches.
                            Only for the owner (the shared view passes no buildRacePlanFn),
                            since publishing writes a link against their account. */}
                        {buildRacePlanFn && (
                          <Box display="flex" alignItems="center" gap={0.5} ml={2}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={watchPlanLoading ? <CircularProgress size={14} /> : <WatchIcon />}
                              onClick={() => handleOpenWatchPlan()}
                              disabled={
                                watchPlanLoading ||
                                watchPlanBuilding ||
                                (!workoutData?.analysis_session_id && !workoutData?.prediction_session_id)
                              }
                              sx={{ fontSize: '0.75rem' }}
                            >
                              {t('watchPlanButton', 'mkpaceLIVE')}
                            </Button>
                            <IconButton
                              size="small"
                              onClick={(e) => setWatchPlanInfoAnchor(e.currentTarget)}
                              sx={{ color: 'text.secondary', p: 0, mb: 1 }}
                            >
                              <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                            <Popover
                              open={Boolean(watchPlanInfoAnchor)}
                              anchorEl={watchPlanInfoAnchor}
                              onClose={() => setWatchPlanInfoAnchor(null)}
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            >
                              <Box sx={{ p: 2, maxWidth: 340 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  {t('watchPlanInfoTitle', 'mkpaceLIVE — plan on your watch')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  {t('watchPlanInfoDesc', 'Publishes this plan to a private URL you paste into the mkpaceLIVE Connect IQ app settings. The watch downloads the plan itself — pace targets, checkpoint stops, cutoffs and elevation for every segment.')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {t('watchPlanInfoGranularity', 'The first publish uses whichever plan the Detailed / Minimal toggle is showing; opening this again just shows you that URL. To send a changed plan you publish a new link, which replaces the old URL — the watch caches the plan on first download, and a new URL is the only thing that makes it fetch again.')}
                                </Typography>
                                <Link
                                  href={MKPACELIVE_MANUAL_URL}
                                  target="_blank"
                                  rel="noopener"
                                  variant="body2"
                                  sx={{ display: 'inline-block', mt: 1.5 }}
                                >
                                  {t('watchPlanManual', 'Open the mkpaceLIVE user manual')} →
                                </Link>
                                <Link
                                  href={MKPACELIVE_QUICKGUIDE_URL}
                                  target="_blank"
                                  rel="noopener"
                                  variant="body2"
                                  sx={{ display: 'block', mt: 0.5 }}
                                >
                                  {t('watchPlanQuickGuide', 'Open the mkpaceLIVE quick guide')} →
                                </Link>
                              </Box>
                            </Popover>
                          </Box>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PrintIcon />}
                          onClick={handlePrintPlan}
                          sx={{ fontSize: '0.75rem', ml: 2 }}
                        >
                          {t('printPlan', 'Print Plan')}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ImageIcon />}
                          onClick={() => downloadPlanWallpaper(displayedSegments, race?.name)}
                          disabled={!displayedSegments?.length}
                          sx={{ fontSize: '0.75rem', ml: 2, minWidth: 0 }}
                        >
                          {!isMobile && t('wallpaper', 'Wallpaper')}
                        </Button>
                        </Box>
                      </Box>

                      <RacePlanGrid
                        segments={displayedSegments}
                        race={race}
                        onAddCheckpoint={buildRacePlanFn ? () => openCheckpointDialog() : null}
                        onInsertCheckpoint={buildRacePlanFn
                          ? (row) => openCheckpointDialog({ distance_m: row.end_distance_m })
                          : null}
                        onEditCheckpoint={(row) => openCheckpointDialog({
                          id: row.manual_end_id,
                          name: row.end_label,
                          distance_m: row.end_distance_m,
                        })}
                        onDeleteCheckpoint={handleDeleteCheckpoint}
                        checkpointBusy={checkpointBusy}
                        workoutStartTime={workoutData.workout_start_time}
                        onSegmentSelect={(seg) => {
                          if (!seg) { setSegmentHighlight(null); return; }
                          setSegmentHighlight({
                            leftKm:  seg.start_distance_m / 1000,
                            rightKm: seg.end_distance_m   / 1000,
                            start_distance_m: seg.start_distance_m,
                            end_distance_m:   seg.end_distance_m,
                          });
                        }}
                        onSegmentDblClick={(seg) => {
                          setSegmentZoom({
                            leftKm:  seg.start_distance_m / 1000,
                            rightKm: seg.end_distance_m   / 1000,
                          });
                        }}
                      />
                    </Box>

                    {/* Off-screen print target — must stay in DOM so Recharts renders */}
                    <Box
                      sx={{
                        position: 'fixed', top: 0, left: '-9999px',
                        width: '960px', zIndex: -1, pointerEvents: 'none',
                      }}
                    >
                      <PlanPrintContent
                        ref={planPrintRef}
                        chunks={chunks}
                        matchedCheckpoints={analyzedCheckpoints}
                        racePlanSegments={displayedSegments}
                        race={race}
                        workoutStartTime={workoutData.workout_start_time}
                        fileSource={workoutData.file_source}
                        deviceName={workoutData.device_name}
                      />
                    </Box>

                  </>
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                    <Typography color={colors.grey[400]}>{t('noRacePlanSegments', 'No race plan segments available.')}</Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Descents Tab */}
            {currentTab === 2 && (
              <Box>
                <Box mb={4}>
                  <RunningRouteMap
                    chunks={chunks}
                    race={race}
                    matchedCheckpoints={analyzedCheckpoints}
                    title={t('descentMapTitle', 'Descent Map')}
                    visibleDistanceRange={chartDistZoom}
                    descentHighlights={descents}
                  />
                </Box>

                <RunningWorkoutChartsSection
                  chunks={chunks}
                  chartType="distance"
                  race={race}
                  matchedCheckpoints={analyzedCheckpoints}
                  externalDistZoom={descentZoom}
                  onDistZoomChange={setChartDistZoom}
                  descents={descents}
                  activeDescentRange={activeDescentRange}
                  defaultMetrics={['altitude', 'moving_desc_vertical_speed']}
                />

                {descents.length > 0 ? (
                  <Box mt={8}>
                    <RunningDescendsGrid
                      descents={descents}
                      title={t('detectedDescents', 'Detected Descents')}
                      onDescentClick={handleDescentClick}
                      onDescentDoubleClick={handleDescentDoubleClick}
                    />
                  </Box>
                ) : (
                  <Box mt={4} p={3} sx={{ backgroundColor: colors.grey[800], borderRadius: '8px', textAlign: 'center' }}>
                    <Typography color={colors.grey[400]}>{t('noDescentsDetected', 'No descents detected in this activity.')}</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default RunningAnalysisView;
